import { useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Users } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Toggle component for enabling/disabling multi-team support.
 * Only visible to Enterprise tier shops.
 */
export function MultiTeamToggle() {
  const { activeShop, refetchMembership } = useAuth();
  const shop = activeShop;
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = useState(false);

  const isEnterprise = shop?.tier === 'ENTERPRISE';
  const isEnabled = shop?.multi_team_enabled === true;

  const handleToggle = async (enabled: boolean) => {
    if (!shop?.id) return;

    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('shops')
        .update({ multi_team_enabled: enabled })
        .eq('id', shop.id);

      if (error) throw error;

      await refetchMembership();
      queryClient.invalidateQueries({ queryKey: ['teams'] });

      toast.success(
        enabled
          ? 'Multi-team support enabled. A default team has been created.'
          : 'Multi-team support disabled.'
      );
    } catch (error: any) {
      toast.error(`Failed to update: ${error.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isEnterprise) {
    return (
      <div className="flex items-center justify-between p-4 rounded-lg border border-dashed">
        <div className="flex items-center gap-3">
          <Users className="h-5 w-5 text-muted-foreground" />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium">Multi-Team Support</span>
              <Badge variant="secondary">Enterprise</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Upgrade to Enterprise to manage multiple service teams.
            </p>
          </div>
        </div>
        <Switch disabled checked={false} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between p-4 rounded-lg border">
        <div className="flex items-center gap-3">
          <Users className="h-5 w-5" />
          <div>
            <div className="flex items-center gap-2">
              <Label htmlFor="multi-team-toggle" className="font-medium cursor-pointer">
                Multi-Team Support
              </Label>
              <Badge variant="outline">Enterprise</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              Enable independent team scheduling and management.
            </p>
          </div>
        </div>
        <Switch
          id="multi-team-toggle"
          checked={isEnabled}
          onCheckedChange={handleToggle}
          disabled={isUpdating}
        />
      </div>

      {isEnabled && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Multi-Team Mode Active</AlertTitle>
          <AlertDescription>
            Tickets, technicians, and bays can now be assigned to specific teams.
            Team-scoped scheduling is enforced for OptiAssign.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
